import MCPLogo from "@/assets/MCPLogo.svg";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { useActiveChatId } from "@/lib/hooks/useActiveChatId";
import { useMcpServers } from "@/lib/hooks/useMcpServers";
import { getStatusColor } from "@/lib/utils";
import { Plug } from "lucide-react";
import type { FpMessagePart } from "../../../../../schemas/codegen";
import { MarkdownSkeleton } from "../../Markdown";
import { SourceCode } from "../../SourceCode";
import { ExpandableMessageCard, SimpleMessageCard } from "./shared";
import { extractToolNameInformation } from "./utils";

interface GenericToolProps {
  part: Extract<FpMessagePart, { type: "tool-call" }>;
}

const McpTool = ({
  part,
  name,
  serverId,
}: GenericToolProps & { name: string; serverId: string }) => {
  const hasArgs = part.args && Object.keys(part.args).length > 0;
  const hasResult = part.result && Object.keys(part.result).length > 0;
  const hasErrorResult = part.status === "error";

  const activeChatId = useActiveChatId();

  const { isLoading, data: connections = {} } = useMcpServers(activeChatId);

  const server = connections[serverId];
  const displayName = (
    <div className="flex flex-basis-1 items-center justify-between gap-2">
      <span>{name}</span>
    </div>
  );

  if (!hasArgs && !hasResult) {
    return (
      <SimpleMessageCard
        status={part.status}
        icon={MCPLogo}
        title={displayName}
      />
    );
  }

  const accordionType = hasArgs && hasResult ? "multiple" : "single";

  const content = (
    <>
      {hasArgs && part.args && (
        <AccordionItem value="args" className="border rounded-md">
          <AccordionTrigger className="hover:no-underline px-3 py-2 hover:bg-muted/30 transition-colors text-sm w-full text-start">
            Args
          </AccordionTrigger>
          <AccordionContent className="min-w-0 ">
            <SourceCode
              content={JSON.stringify(part.args, null, 2)}
              enableOverflow
              SkeletonComponent={MarkdownSkeleton}
              language="json"
            />
          </AccordionContent>
        </AccordionItem>
      )}
      {hasResult && !hasErrorResult ? (
        <AccordionItem value="result" className="border rounded-md">
          <AccordionTrigger className="hover:no-underline px-3 py-2 hover:bg-muted/30 transition-colors text-sm w-full text-start">
            Result
          </AccordionTrigger>
          <AccordionContent className="min-w-0">
            {part.status === "complete" && hasResult && part.result ? (
              <SourceCode
                content={JSON.stringify(part.result, null, 2)}
                enableOverflow
                SkeletonComponent={MarkdownSkeleton}
                language="json"
              />
            ) : null}
          </AccordionContent>
        </AccordionItem>
      ) : null}
    </>
  );

  return (
    <ExpandableMessageCard
      status={part.status}
      result={part.result}
      icon={MCPLogo}
      title={displayName}
      additionalContent={
        !isLoading && (
          <div
            className="text-muted-foreground flex flex-grow items-center gap-2 justify-end"
            title={`id: ${serverId} ${server ? `(status: ${server.connectionState}})` : ""}`}
          >
            <div
              className={`h-2 w-2 shrink-0 rounded-full ${(server && getStatusColor(server.connectionState)) || "bg-muted-foreground"}`}
            />
            {server ? server.name : "Connection removed"}
          </div>
        )
      }
      accordionValue="mcp-tool"
      className="min-w-0"
    >
      {accordionType === "single" ? (
        <Accordion
          type="single"
          collapsible
          className="min-w-0"
          defaultValue={hasArgs ? "args" : "result"}
        >
          {content}
        </Accordion>
      ) : (
        <Accordion
          type="multiple"
          className="min-w-0"
          defaultValue={[hasResult ? "result" : "args"]}
        >
          {content}
        </Accordion>
      )}
    </ExpandableMessageCard>
  );
};

export function GenericTool({ part }: GenericToolProps) {
  const isError = part.status === "error";

  const { serverId, name } = extractToolNameInformation(part.toolName);
  if (serverId && name) {
    return <McpTool part={part} name={name} serverId={serverId} />;
  }

  const activity =
    part.status === "pending"
      ? "Calling"
      : part.status === "complete"
        ? "Called"
        : "Failed:";
  const displayName = (
    <span className="inline-flex items-center gap-2">
      <span className="text-muted-foreground">{activity}</span>
      <span>{part.toolName}</span>
    </span>
  );

  if (isError) {
    return (
      <ExpandableMessageCard
        status={part.status}
        result={part.result}
        icon={Plug}
        title={displayName}
        accordionValue="generic-tool"
      />
    );
  }

  return (
    <SimpleMessageCard status={part.status} icon={Plug} title={displayName} />
  );
}
